import { authenticatedFetch } from "@lumi/data";
import { useCallback } from "react";
import { useAuth } from "../../../../auth/AuthContext";
import { createRefreshCallback } from "../../../../auth/refreshCallback";
import { useToast } from "../../../../shared/hooks/use-toast";
import type { BulkEditTitle } from "../types";

interface UseBulkActionsProps {
  readonly titles: BulkEditTitle[];
  readonly selectedIds: Set<string>;
  readonly setSelectedIds: (ids: Set<string>) => void;
  readonly updateField: (titleId: string, field: string, value: string | number | boolean | null) => Promise<void>;
  readonly updateTitleLocally: (titleId: string, updates: Partial<BulkEditTitle>) => void;
  readonly refetch: () => Promise<void>;
}

/**
 * Hook for bulk publish operation
 */
function useBulkPublish({
  selectedIds,
  setSelectedIds,
  updateField,
  updateTitleLocally,
}: Pick<UseBulkActionsProps, "selectedIds" | "setSelectedIds" | "updateField" | "updateTitleLocally">) {
  const { toast } = useToast();

  return useCallback(() => {
    const count = selectedIds.size;
    void (async () => {
      try {
        await Promise.all(Array.from(selectedIds).map((id) => updateField(id, "published", true)));
        for (const id of selectedIds) {
          updateTitleLocally(id, { published: true });
        }
        toast({ description: `Published ${count} title${count > 1 ? "s" : ""}` });
        setSelectedIds(new Set());
      } catch (err) {
        console.error("Failed to publish titles:", err);
        toast({ description: "Failed to publish titles", variant: "destructive" });
      }
    })();
  }, [selectedIds, updateField, updateTitleLocally, toast, setSelectedIds]);
}

/**
 * Hook for bulk unpublish operation
 */
function useBulkUnpublish({
  selectedIds,
  setSelectedIds,
  updateField,
  updateTitleLocally,
}: Pick<UseBulkActionsProps, "selectedIds" | "setSelectedIds" | "updateField" | "updateTitleLocally">) {
  const { toast } = useToast();

  return useCallback(() => {
    const count = selectedIds.size;
    void (async () => {
      try {
        await Promise.all(Array.from(selectedIds).map((id) => updateField(id, "published", false)));
        for (const id of selectedIds) {
          updateTitleLocally(id, { published: false });
        }
        toast({ description: `Unpublished ${count} title${count > 1 ? "s" : ""}` });
        setSelectedIds(new Set());
      } catch (err) {
        console.error("Failed to unpublish titles:", err);
        toast({ description: "Failed to unpublish titles", variant: "destructive" });
      }
    })();
  }, [selectedIds, updateField, updateTitleLocally, toast, setSelectedIds]);
}

/**
 * Hook for bulk delete operation
 */
function useBulkDelete({
  selectedIds,
  setSelectedIds,
  refetch,
}: Pick<UseBulkActionsProps, "selectedIds" | "setSelectedIds" | "refetch">) {
  const { accessToken, refreshAuth } = useAuth();
  const { toast } = useToast();

  return useCallback(() => {
    if (selectedIds.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${selectedIds.size} title(s)? This action cannot be undone.`)) {
      return;
    }

    void (async () => {
      if (!accessToken) {
        toast({ description: "Not authenticated", variant: "destructive" });
        return;
      }

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
        const titleIds = Array.from(selectedIds);

        const res = await authenticatedFetch<{ deleted: number }>(
          `${baseUrl}/v1/admin/titles/bulk-delete`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ titleIds }),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        await refetch();
        setSelectedIds(new Set());

        toast({ description: `Deleted ${res.deleted} title${res.deleted > 1 ? "s" : ""}` });
      } catch (err) {
        console.error("Bulk delete failed:", err);
        toast({
          description: err instanceof Error ? err.message : "Failed to delete titles",
          variant: "destructive",
        });
      }
    })();
  }, [selectedIds, accessToken, refreshAuth, refetch, setSelectedIds, toast]);
}

/**
 * Hook for CSV export operation
 */
function useBulkExportCsv({ titles, selectedIds }: Pick<UseBulkActionsProps, "titles" | "selectedIds">) {
  const { toast } = useToast();

  return useCallback(() => {
    const selectedTitles = titles.filter((t) => selectedIds.has(t.id));
    const csv = [
      ["ID", "Title", "Year", "Director", "Runtime", "Genres", "Published"].join(","),
      ...selectedTitles.map((t) =>
        [
          t.id,
          `"${t.name}"`,
          t.year || "",
          `"${t.directorName || ""}"`,
          t.runtimeMinutes || "",
          `"${t.genres.join(", ")}"`,
          t.published ? "Yes" : "No",
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `titles-export-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast({ description: `Exported ${selectedTitles.length} title${selectedTitles.length > 1 ? "s" : ""} to CSV` });
  }, [titles, selectedIds, toast]);
}

/**
 * Combines all bulk action hooks
 * Note: Add-to-collection is handled directly in BulkEditTitles.tsx with CollectionPickerModal
 */
export function useBulkActions(props: UseBulkActionsProps) {
  const handleBulkPublish = useBulkPublish(props);
  const handleBulkUnpublish = useBulkUnpublish(props);
  const handleBulkDelete = useBulkDelete(props);
  const handleBulkExportCsv = useBulkExportCsv(props);

  return {
    handleBulkPublish,
    handleBulkUnpublish,
    handleBulkDelete,
    handleBulkExportCsv,
  };
}
